import React, { useState, useEffect } from "react";
import { FiLinkedin, FiZap, FiRefreshCw, FiInbox, FiInfo, FiX, FiHelpCircle } from "react-icons/fi";
import toast from "react-hot-toast";

import API from "@/services/api";
import useStore from "@/services/store";
import Loader from "@/components/loader";

const BANNER_KEY = "linkedin-rotation-banner-dismissed";

const formatHours = (h) => {
  if (h == null) return "—";
  if (h < 1) return "<1h";
  if (h < 24) return `${Math.round(h)}h`;
  const days = Math.floor(h / 24);
  const rem = Math.round(h % 24);
  return rem ? `${days}d ${rem}h` : `${days}d`;
};

const formatRelative = (date) => {
  if (!date) return "—";
  const diffMs = Date.now() - new Date(date).getTime();
  const h = diffMs / (1000 * 3600);
  if (h < 1) return "less than an hour ago";
  if (h < 24) return `${Math.round(h)}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
};

const formatRelativeShort = (date) => {
  if (!date) return "—";
  const diffMs = Date.now() - new Date(date).getTime();
  const h = diffMs / (1000 * 3600);
  if (h < 1) return "<1h";
  if (h < 24) return `${Math.round(h)}h`;
  const days = Math.floor(h / 24);
  return `${days}d`;
};

const formatEtaRange = (h) => {
  if (h == null || h <= 0) return "imminent";
  const days = h / 24;
  if (days < 1) return `~${Math.round(h)}h`;
  const low = Math.max(1, Math.floor(days));
  const high = Math.ceil(days) + (days - Math.floor(days) > 0 ? 0 : 1);
  if (low === high) return `~${low} day${low > 1 ? "s" : ""}`;
  return `~${low}–${high} days`;
};

const getRunNumber = (label) => {
  if (!label) return null;
  const m = label.match(/\d+/);
  return m ? parseInt(m[0], 10) : null;
};

const reasonLabel = (reason) => {
  switch (reason) {
    case "max_applicants": return "hit 50 candidates";
    case "max_duration": return "5-day max reached";
    case "job_unpublished": return "job unpublished";
    case "unmanaged_org": return "org not eligible";
    case "duplicate_cleanup": return "duplicate cleaned up";
    case "slot_full": return "legacy rotation";
    case "rotated_out_72h": return "legacy rotation";
    default: return reason || "—";
  }
};

export default function LinkedInRotation() {
  const { organization } = useStore();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(BANNER_KEY) === "1";
  });

  useEffect(() => {
    if (organization?._id) fetchData();
  }, [organization?._id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await API.post("/post/linkedin-rotation", { organization_id: organization._id });
      if (res.ok) setData(res.data);
      else toast.error("Couldn't load LinkedIn state");
    } catch (e) {
      console.error(e);
      toast.error("Loading error");
    } finally {
      setLoading(false);
    }
  };

  const toggleFastTrack = async (post, value) => {
    try {
      const res = await API.put(`/post/${post._id}`, { priority: value });
      if (res.ok) {
        toast.success(
          value
            ? `Fast-track activated for "${post.job_title}"`
            : `Fast-track cancelled for "${post.job_title}"`,
          { icon: value ? "⚡" : "↩️" },
        );
        await fetchData();
      } else {
        toast.error("Couldn't update fast-track");
      }
    } catch (err) {
      if (err?.code === "FAST_TRACK_LIMIT") {
        toast.error(err.message || "Only one fast-track per organization.");
      } else {
        console.error(err);
        toast.error("Network error");
      }
    }
  };

  const dismissBanner = () => {
    window.localStorage.setItem(BANNER_KEY, "1");
    setBannerDismissed(true);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader />
      </div>
    );
  }

  if (!data) {
    return <div className="p-6 text-gray-500">No data.</div>;
  }

  const { config, stats, active, queue, exited } = data;
  const slotsFull = stats.slots_used >= stats.slots_total;
  const noActiveForMe = active.length === 0;

  return (
    <div className="p-6 space-y-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FiLinkedin className="text-blue-600" />
            LinkedIn rotation
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {Math.round(config.rotation_hours / 24)} days or {config.max_applicants} candidates per run · {config.slots_total} shared slots
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Stat
            label="Slots in use"
            value={`${stats.slots_used} / ${stats.slots_total}`}
            tooltip={`LinkedIn ingests at most ${stats.slots_total} of our jobs at a time. This pool is shared across all organizations.`}
          />
          <Stat
            label="Global queue"
            value={`${stats.queue_total} job${stats.queue_total > 1 ? "s" : ""}`}
            tooltip="All jobs across all organizations waiting for a slot. Sorted by fast-track first, then oldest-published first."
          />
          <button onClick={fetchData} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50" title="Refresh">
            <FiRefreshCw className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>

      {!bannerDismissed && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <FiInfo className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-blue-900">
            <p className="font-medium mb-1">How LinkedIn rotation works</p>
            <p className="text-blue-800 leading-relaxed">
              We share <strong>{config.slots_total} LinkedIn slots</strong> across organizations.
              Your published jobs queue up <strong>oldest first</strong>; each run lasts up to{" "}
              <strong>{Math.round(config.rotation_hours / 24)} days</strong> or until it hits{" "}
              <strong>{config.max_applicants} candidates</strong> — whichever comes first.
              Each job can run up to <strong>{config.max_runs_per_job} times</strong> on LinkedIn.
              Need it live now? Request a <strong>fast-track</strong> to jump to the front of the queue.
            </p>
          </div>
          <button onClick={dismissBanner} className="p-1 rounded hover:bg-blue-100 shrink-0" title="Dismiss">
            <FiX className="w-4 h-4 text-blue-700" />
          </button>
        </div>
      )}

      {/* My active jobs */}
      <Section title="My jobs live on LinkedIn" count={active.length}>
        {active.length === 0 ? (
          <EmptyRow
            message={
              slotsFull
                ? `None of your jobs are on LinkedIn right now — all ${stats.slots_total} shared slots are taken by other organizations. They'll free up as runs end.`
                : "None of your jobs are currently on LinkedIn."
            }
          />
        ) : (
          active.map((p) => {
            const runN = getRunNumber(p.label);
            const isFinalRun = runN === config.max_runs_per_job;
            return (
              <Row key={p._id}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900 truncate">{p.job_title}</span>
                      {p.label && (
                        <span
                          className="text-xs text-gray-400"
                          title={`Run ${runN} of ${config.max_runs_per_job}. Each job gets up to ${config.max_runs_per_job} LinkedIn runs.`}
                        >
                          · {p.label}/{config.max_runs_per_job}
                        </span>
                      )}
                      {isFinalRun && (
                        <Pill tone="amber" title={`This is the last of ${config.max_runs_per_job} runs for this job. After it ends, the job won't go back on LinkedIn automatically.`}>
                          Final run
                        </Pill>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5">
                      Live for {formatHours(p.age_hours)} · {p.run_applicants}/{config.max_applicants} candidates this run · {p.viewed_count} views · ends in {formatHours(p.remaining_hours)}
                    </p>
                  </div>
                </div>
              </Row>
            );
          })
        )}
      </Section>

      {/* My queued jobs */}
      <Section title="My jobs in queue" count={queue.length}>
        {queue.length === 0 ? (
          <EmptyRow message="None of your jobs are waiting for a slot." />
        ) : (
          queue.map((p) => {
            const isFastTracked = !!p.priority;
            const runN = getRunNumber(p.label);
            const isFinalRun = runN === config.max_runs_per_job;
            return (
              <Row key={p._id}>
                <div className="flex items-center gap-3 min-w-0">
                  <PositionBadge
                    position={p.position}
                    total={stats.queue_total}
                    fastTracked={isFastTracked}
                  />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900 truncate">{p.job_title}</span>
                      {p.label && (
                        <span className="text-xs text-gray-400">
                          · {p.label}/{config.max_runs_per_job}
                        </span>
                      )}
                      {isFastTracked && (
                        <Pill tone="amber" icon={<FiZap className="w-3 h-3" />} title="Fast-tracked — this job jumps to the top of the queue.">
                          Fast-tracked
                        </Pill>
                      )}
                      {isFinalRun && (
                        <Pill tone="amber" title={`This is the last of ${config.max_runs_per_job} runs for this job.`}>
                          Final run
                        </Pill>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5">
                      #{p.position} of {stats.queue_total} in the global queue · queued for {formatRelativeShort(p.created_at)} ·{" "}
                      <span title="Estimate. The queue moves when slots free up, so this can shift.">ETA {formatEtaRange(p.eta_hours)}</span>
                    </p>
                  </div>
                </div>
                {isFastTracked ? (
                  <button
                    onClick={() => toggleFastTrack(p, false)}
                    className="px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 shrink-0"
                    title="Cancel the fast-track and return to the normal FIFO position."
                  >
                    Cancel fast-track
                  </button>
                ) : (
                  <button
                    onClick={() => toggleFastTrack(p, true)}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 shrink-0"
                    title="Skip the queue and go live as soon as a slot frees up."
                  >
                    Request fast-track
                  </button>
                )}
              </Row>
            );
          })
        )}
      </Section>

      {/* Recent runs */}
      <Section title="Recent runs" count={exited.length}>
        {exited.length === 0 ? (
          <EmptyRow message="No recent runs." />
        ) : (
          exited.map((p) => (
            <Row key={p._id}>
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-2 h-2 rounded-full bg-gray-300 shrink-0" />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-900 truncate">{p.job_title}</span>
                    {p.label && (
                      <span className="text-xs text-gray-400">
                        · {p.label}/{config.max_runs_per_job}
                      </span>
                    )}
                    <Pill tone="gray">Ended</Pill>
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Ran for {formatHours(p.age_hours)} · {p.run_applicants}/{config.max_applicants} candidates · {p.viewed_count} views · ended {formatRelative(p.updated_at)} ({reasonLabel(p.rotation_reason)})
                  </p>
                </div>
              </div>
            </Row>
          ))
        )}
      </Section>
    </div>
  );
}

const Stat = ({ label, value, tooltip }) => (
  <div
    className="bg-white border border-gray-200 rounded-lg px-4 py-2.5"
    title={tooltip}
  >
    <p className="text-xs text-gray-500 flex items-center gap-1">
      {label}
      {tooltip && <FiHelpCircle className="w-3 h-3 text-gray-400" />}
    </p>
    <p className="text-base font-semibold text-gray-900">{value}</p>
  </div>
);

const Section = ({ title, count, children }) => (
  <section>
    <h2 className="text-xs font-semibold tracking-wider text-gray-500 uppercase mb-3">
      {title} {count > 0 && <span className="text-gray-400 font-normal">· {count}</span>}
    </h2>
    <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
      {children}
    </div>
  </section>
);

const Row = ({ children }) => (
  <div className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
    {children}
  </div>
);

const EmptyRow = ({ message }) => (
  <div className="flex items-center gap-3 px-4 py-6 text-sm text-gray-500">
    <FiInbox className="w-4 h-4 shrink-0" />
    <span>{message}</span>
  </div>
);

const Pill = ({ tone = "gray", icon, children, title }) => {
  const tones = {
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    gray: "bg-gray-100 text-gray-600 border-gray-200",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium border rounded-full ${tones[tone]}`}
      title={title}
    >
      {icon}
      {children}
    </span>
  );
};

const PositionBadge = ({ position, total, fastTracked }) => (
  <span
    title={`Position ${position} of ${total} in the global queue`}
    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 ${
      fastTracked ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-gray-100 text-gray-700"
    }`}
  >
    {fastTracked ? (
      <span className="flex items-center gap-0.5">
        {position}
        <FiZap className="w-3 h-3" />
      </span>
    ) : (
      position
    )}
  </span>
);
